import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Radio,
  RadioGroup
} from "@heroui/react";
import { MailIcon } from '@heroicons/react/outline';

interface EmailProtectionButtonProps {
  email: string | null | undefined;
  buttonText?: string;
  ownerName?: string;
  className?: string;
  buttonSize?: "sm" | "md" | "lg";
  buttonVariant?: "solid" | "bordered" | "light" | "flat" | "faded" | "shadow" | "ghost";
  buttonColor?: "default" | "primary" | "secondary" | "success" | "warning" | "danger";
}

/**
 * EmailProtectionButton - A reusable component for protecting email addresses from scrapers
 *
 * This component shows a button that, when clicked, displays a modal with human verification
 * and gradually reveals the email address to protect it from bots and scrapers.
 */
export default function EmailProtectionButton({
  email,
  buttonText = "Napisz",
  ownerName = "właściciela",
  className = "",
  buttonSize = "sm",
  buttonVariant = "solid",
  buttonColor = "primary"
}: EmailProtectionButtonProps) {
  // Modal control
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Verification states
  const [selectedOption, setSelectedOption] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  // Generate a random simple math question for human verification
  const [mathQuestion, setMathQuestion] = useState(() => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    return {
      question: `Ile to jest ${num1} + ${num2}?`,
      answer: (num1 + num2).toString()
    };
  });
  // Handle verification submission
  const handleVerify = () => {
    if (selectedOption === mathQuestion.answer) {
      setEmailVerified(true);
    } else {
      // Reset the question if the answer is wrong
      const num1 = Math.floor(Math.random() * 10);
      const num2 = Math.floor(Math.random() * 10);
      setMathQuestion({
        question: `Ile to jest ${num1} + ${num2}?`,
        answer: (num1 + num2).toString()
      });
      setSelectedOption("");
    }
  };
  // Reset states when opening modal
  const handleOpen = () => {
    onOpen();
    setEmailVerified(false);
    setEmailRevealed(false);
    // Generate a new math question each time
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setMathQuestion({
      question: `Ile to jest ${num1} + ${num2}?`,
      answer: (num1 + num2).toString()
    });
    setSelectedOption("");
  };

  // Create obfuscated email display to prevent scraping
  const getObfuscatedEmail = (emailToObfuscate: string | null | undefined) => {
    if (!emailToObfuscate) return "Brak adresu email";

    const parts = emailToObfuscate.split('@');
    if (parts.length !== 2) return emailToObfuscate;

    const username = parts[0];
    const domain = parts[1];

    const hiddenUsername = username.length > 3
      ? username.slice(0, 2) + '●'.repeat(username.length - 3) + username.slice(-1)
      : username.slice(0, 1) + '●'.repeat(username.length - 1);

    return `${hiddenUsername}@${domain}`;
  };

  // Safely reveal the email
  const revealEmail = () => {
    setEmailRevealed(true);
  };

  return (
    <>
      <Button
        className={`${className} transition-all duration-300`}
        size={buttonSize}
        color={buttonColor}
        variant={buttonVariant}
        startContent={<MailIcon className="h-4 w-4" />}
        onClick={handleOpen}
        isDisabled={!email}
      >
        {buttonText}
      </Button>

      {/* Email Reveal Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        // Use standard values for properties that might be causing issues
        backdrop="opaque"
        scrollBehavior="inside"
        // Prevent undefined being passed to classNames
        classNames={{
          backdrop: "bg-gradient-to-t from-zinc-900/50 to-zinc-900/30",
          base: "border-gray-200",
          header: "border-b-1 border-gray-200",
          footer: "border-t-1 border-gray-200",
          body: "py-6",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Kontakt z {ownerName}
          </ModalHeader>
          <ModalBody>
            {!emailVerified ? (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Aby zobaczyć adres email, prosimy o potwierdzenie, że jesteś człowiekiem.
                </p>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">{mathQuestion.question}</p>

                  <RadioGroup
                    value={selectedOption}
                    onValueChange={setSelectedOption}
                    className="mt-3"
                    orientation="horizontal"
                  >
                    {/* Generate 4 options including the correct answer */}
                    {[
                      mathQuestion.answer,
                      (parseInt(mathQuestion.answer) + 1).toString(),
                      (parseInt(mathQuestion.answer) - 1).toString(),
                      (parseInt(mathQuestion.answer) + 2).toString()
                    ].sort(() => Math.random() - 0.5).map((option) => (
                      <Radio key={option} value={option}>
                        {option}
                      </Radio>
                    ))}
                  </RadioGroup>
                </div>
                <div className="flex justify-end">
                  <Button color="primary" onClick={handleVerify}>
                    Weryfikuj
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 font-medium">Weryfikacja udana!</p>
                </div>
                {!emailRevealed ? (
                  <>
                    <p className="text-gray-700">
                      Adres email jest częściowo ukryty, aby chronić użytkownika przed spamem.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <p className="text-gray-900 font-medium">
                        {getObfuscatedEmail(email)}
                      </p>
                    </div>
                    <div className="flex justify-end">
                      <Button color="primary" onClick={revealEmail}>
                        Pokaż pełny adres
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-gray-700">
                      Możesz teraz skontaktować się poprzez email:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                      <p className="text-gray-900 font-medium break-words max-w-[60%]">
                        {email || "Brak publicznego adresu email"}
                      </p>
                      <Button
                        size="sm"
                        as="a"
                        href={`mailto:${email}`}
                        color="primary"
                        variant="flat"
                        isDisabled={!email}
                      >
                        Wyślij email
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      Pamiętaj, aby w temacie wiadomości podać powód kontaktu.
                    </p>
                  </>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onClick={onClose}>
              Zamknij
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}